import React, { useState, useEffect } from 'react';
import { Html5QrcodeScanner } from 'html5-qrcode';
import LocationDropdown from '../../../tabs/Dropdown/LocationName';
import axiosInstance from '../../../../../api';

// CaseEditmodal component for adding/editing EMS cases
const CaseEditmodal = ({ show, onClose, formData, onChange, onSave, isEditMode, isLoading }) => {
  // State for showing/hiding QR code scanner
  const [showScanner, setShowScanner] = useState(false);
  // State for form validation errors
  const [errors, setErrors] = useState({});
  // State for selected location
  const [selectedLocation, setSelectedLocation] = useState(formData.case_location || '');
  // State for selected area
  const [selectedArea, setSelectedArea] = useState(formData.case_area || '');
  // State for tracking file input fields
  const [fileInputs, setFileInputs] = useState([]);
  // State for storing medical centers list
  const [medicalCenters, setMedicalCenters] = useState([]);
  // State for storing areas filtered by location
  const [areas, setAreas] = useState([]);
  // State for storing all available areas
  const [allAreas, setAllAreas] = useState([]);
  // State for loading status of areas
  const [loadingAreas, setLoadingAreas] = useState(false);
  // State for area loading errors
  const [areaError, setAreaError] = useState(null);
  // State for transfer from medical center
  const [transferFromMedicalCenter, setTransferFromMedicalCenter] = useState(formData.transfer_from_medical_center || '');
  // State for medical centers loading status
  const [isMedicalCentersLoading, setIsMedicalCentersLoading] = useState(false);

  // Maximum number of file inputs allowed
  const MAX_FILES = 3;

  // Predefined list of injury types
  const injuryTypes = [
    'Fracture', 'Laceration', 'Burn', 'Sprain', 'Strain', 
    'Concussion', 'Dislocation', 'Abrasion', 'Contusion', 
    'Puncture Wound', 'Amputation', 'Crush Injury'
  ];

  // useEffect for QR code scanner initialization and cleanup
  useEffect(() => {
    let scanner;
    if (showScanner) {
      // Initialize QR code scanner
      scanner = new Html5QrcodeScanner("reader", { fps: 10, qrbox: 250 });
      scanner.render((decodedText) => {
        // Update bracelet ID with scanned value
        onChange({ target: { name: 'bracelet_id', value: decodedText } });
        setShowScanner(false);
        scanner.clear();
      });
    }

    // Cleanup function to clear scanner
    return () => {
      if (scanner) scanner.clear();
    };
  }, [showScanner, onChange]);

  // useEffect for initializing form data when modal is shown
  useEffect(() => {
    if (show) {
      // Set initial values from props
      setSelectedLocation(formData.case_location || '');
      setSelectedArea(formData.case_area || '');
      setTransferFromMedicalCenter(formData.transfer_from_medical_center || '');
      setErrors({});
      
      // Set up file inputs based on existing documents
      if (isEditMode && formData.documents) {
        const docKeys = Object.keys(formData.documents).filter(key => key.startsWith('documents'));
        const docCount = docKeys.length;
        if (docCount > 0) {
          setFileInputs(Array.from({ length: Math.min(docCount, MAX_FILES) }, (_, i) => i));
        } else {
          setFileInputs([0]);
        }
      } else {
        setFileInputs([0]);
      }
    }
  }, [show, formData, isEditMode]);

  // useEffect for fetching all areas (only in add mode)
  useEffect(() => {
    const fetchAllAreas = async () => {
      try {
        setLoadingAreas(true);
        setAreaError(null);
        const response = await axiosInstance.get('/ems/areas');
        setAllAreas(response.data || []);
      } catch (err) {
        console.error('Error fetching areas:', err);
        setAreaError('Failed to load areas. Please try again.');
      } finally {
        setLoadingAreas(false);
      }
    };

    if (!isEditMode) {
      fetchAllAreas();
    }
  }, [isEditMode]);

  // useEffect for fetching medical centers based on selected area
  useEffect(() => {
    const fetchMedicalCenters = async () => {
      if (!selectedArea) {
        setMedicalCenters([]);
        return;
      }

      try {
        setIsMedicalCentersLoading(true);
        const response = await axiosInstance.get(`/ems/resource-avilability-check/${selectedArea}`);
        if (response.data?.status === 'success') {
          const centers = response.data.data?.medical_centers || [];
          setMedicalCenters(centers);
        }
      } catch (err) {
        if (err.response && err.response.status === 404) {
          const { status, message } = err.response.data;
          console.log(`${status} - ${message}`);
          setMedicalCenters([]);
        } else {
          console.error('Unexpected error:', err);
          setMedicalCenters([]);
        }
      } finally {
        setIsMedicalCentersLoading(false);
      }
    };

    if (!isEditMode && selectedArea) {
      fetchMedicalCenters();
    }
  }, [selectedArea, isEditMode]);

  // useEffect for filtering areas based on selected location
  useEffect(() => {
    if (!selectedLocation) {
      setAreas([]);
      setSelectedArea('');
      onChange({ target: { name: 'case_area', value: '' } });
      onChange({ target: { name: 'nearest_medical_center', value: '' } });
      return;
    }
    const filtered = allAreas.filter(area => area.location_id == selectedLocation);
    setAreas(filtered);
  }, [selectedLocation, allAreas]);

  // Handler for closing the modal
  const handleClose = () => {
    onClose();
  };

  // Handler for location change
  const handleLocationChange = (e) => {
    if (!isEditMode) {
      const value = e.target.value;
      setSelectedLocation(value);
      setSelectedArea('');
      onChange({ target: { name: 'case_location', value } });
      onChange({ target: { name: 'case_area', value: '' } });
      onChange({ target: { name: 'nearest_medical_center', value: '' } });

      if (value) {
        setErrors(prev => ({ ...prev, case_location: '' }));
      } else {
        setErrors(prev => ({ ...prev, 
          case_location: 'Please select a location', 
          case_area: 'Please select a location first' 
        }));
      }
    }
  };

  // Handler for area change
  const handleAreaChange = async (e) => {
    if (!isEditMode) {
      const value = e.target.value;
      setSelectedArea(value);
      onChange({ target: { name: 'case_area', value } });
      onChange({ target: { name: 'nearest_medical_center', value: '' } });

      if (value) {
        setErrors(prev => ({ ...prev, case_area: '' }));
      } else {
        setMedicalCenters([]);
      }
    }
  };

  // Handler for general input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    onChange(e);
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  // Handler for file input changes
  const handleFileChange = (e, index) => {
    const file = e.target.files[0];
    const newFiles = { ...(formData.documents || {}) };
    
    if (file) {
      const validTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/jfif'];
      if (!validTypes.includes(file.type)) {
        setErrors(prev => ({
          ...prev,
          [`documents${index + 1}`]: 'Only PDF, JPG, JPEG, PNG, WEBP, and JFIF files are allowed'
        }));
        return;
      }
      
      newFiles[`documents${index + 1}`] = file;
    } else {
      delete newFiles[`documents${index + 1}`];
    }
    
    onChange({ target: { name: 'documents', value: newFiles } });
    setErrors(prev => ({ ...prev, [`documents${index + 1}`]: '' }));
  };

  // Function to add a new file input
  const addFileInput = () => {
    if (fileInputs.length < MAX_FILES) {
      const newIndex = fileInputs.length;
      setFileInputs([...fileInputs, newIndex]);
    }
  };

  // Function to remove a file input
  const removeFileInput = (index) => {
    const newFileInputs = fileInputs.filter((_, i) => i !== index);
    setFileInputs(newFileInputs.map((_, i) => i));
    
    const newFiles = { ...(formData.documents || {}) };
    delete newFiles[`documents${index + 1}`];
    
    const updatedFiles = {};
    let counter = 1;
    Object.values(newFiles).forEach((file) => {
      if (file) {
        updatedFiles[`documents${counter}`] = file;
        counter++;
      }
    });
    
    onChange({ target: { name: 'documents', value: updatedFiles } });
    
    if (newFileInputs.length === 0 && MAX_FILES > 0) {
      setFileInputs([0]);
    }
  };

  // Function to validate the form
  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    if (!formData.case_name?.trim()) {
      newErrors.case_name = 'Case name is required';
      isValid = false;
    }
    
    if (!isEditMode && formData.case_status !== 2) {
      if (!selectedLocation) {
        newErrors.case_location = 'Location is required';
        isValid = false;
      }
      if (!selectedArea) {
        newErrors.case_area = 'Area is required';
        isValid = false;
      }
    }
    
    if (!formData.nearest_medical_center) {
      newErrors.nearest_medical_center = 'Medical center is required';
      isValid = false;
    }
    if (!formData.injury_type?.trim()) {
      newErrors.injury_type = 'Type of injury is required';
      isValid = false;
    }
    
    if (!formData.contact_number?.trim()) {
      newErrors.contact_number = 'Contact number is required';
      isValid = false;
    } else if (!/^[0-9]{10,15}$/.test(formData.contact_number)) {
      newErrors.contact_number = 'Contact number must be 10-15 digits';
      isValid = false;
    }

    if (isEditMode) {
      if (!formData.triage_card) {
        newErrors.triage_card = 'Triage card is required';
        isValid = false;
      }
      if (!formData.bracelet_id?.trim()) {
        newErrors.bracelet_id = 'Bracelet ID is required';
        isValid = false;
      }
      if (formData.documents) {
        Object.entries(formData.documents).forEach(([key, file], index) => {
          if (file && !['application/pdf', 'image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/jfif'].includes(file.type)) {
            newErrors[key] = 'Only PDF, JPG, JPEG, PNG, WEBP, and JFIF files are allowed';
            isValid = false;
          }
        });
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  // Handler for save button click
  const handleSave = (e) => {
    e.preventDefault();
    if (validateForm()) {
      const formDataToSend = new FormData();
      formDataToSend.append('case_name', formData.case_name || '');
      
      if (formData.case_status !== 2) {
        formDataToSend.append('case_location', selectedLocation);
        formDataToSend.append('case_area', selectedArea);
      }
      
      formDataToSend.append('nearest_medical_center', formData.nearest_medical_center || '');
      formDataToSend.append('injury_type', formData.injury_type || '');
      formDataToSend.append('age', formData.age || '');
      formDataToSend.append('gender', formData.gender || '');
      formDataToSend.append('contact_number', formData.contact_number || '');

      if (isEditMode) {
        formDataToSend.append('triage_card', formData.triage_card || '');
        formDataToSend.append('bracelet_id', formData.bracelet_id || '');
        formDataToSend.append('_method', 'PUT');
      }

      if (formData.documents) {
        Object.entries(formData.documents).forEach(([key, file]) => {
          if (file instanceof File) {
            formDataToSend.append(key, file);
          }
        });
      }

      onSave(formDataToSend);
    }
  };

  // Don't render if modal is not shown
  if (!show) return null;

  return (
    <>
      {/* Main modal */}
      <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', overflowY: 'auto' }}>
        <div className="modal-dialog modal-dialog-scrollable modal-lg">
          <div className="modal-content">
            {/* Modal header */}
            <div className="modal-header d-flex align-items-center">
              <h5 className="modal-title">{isEditMode ? 'Edit EMS Case' : 'Add EMS Case'}</h5>
              <button type="button" className="btn-close" onClick={handleClose} aria-label="Close"></button>
            </div>
            {/* Modal body */}
            <div className="modal-body">
              <div className="row">
                {/* Case Name field */}
                <div className="col-md-6">
                  <div className="mb-3">
                    <label>Case Name <span className="text-danger">*</span></label>
                    <input 
                      type="text" 
                      className={`form-control ${errors.case_name ? 'is-invalid' : ''}`}
                      name="case_name"
                      value={formData.case_name || ''}
                      onChange={handleChange}
                      required
                    />
                    {errors.case_name && <div className="invalid-feedback">{errors.case_name}</div>}
                  </div>
                </div>
                
                {/* Transfer To field (only for transfer cases) */}
                {formData.case_status === 2 && (
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label>Transfer To<span className="text-danger">*</span></label>
                      <input 
                        type="text" 
                        className={`form-control ${errors.nearest_medical_center ? 'is-invalid' : ''}`}
                        value={formData.medical_center?.name || ''}
                        readOnly
                      />
                      {errors.nearest_medical_center && <div className="invalid-feedback">{errors.nearest_medical_center}</div>}
                    </div>
                  </div>
                )}
              
                {/* Location field (only for non-transfer cases) */}
                {formData.case_status !== 2 && (
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label>Location <span className="text-danger">*</span></label>
                      {isEditMode ? (
                        <input 
                          type="text" 
                          className={`form-control ${errors.case_location ? 'is-invalid' : ''}`}
                          value={formData.location?.location_name || formData.case_location || ''}
                          disabled
                        />
                      ) : (
                        <LocationDropdown
                          value={selectedLocation}
                          onChange={handleLocationChange}
                          className={errors.case_location ? 'is-invalid' : ''}
                          required
                        />
                      )}
                      {errors.case_location && <div className="invalid-feedback">{errors.case_location}</div>}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Area and Medical Center fields (only for non-transfer cases) */}
              {formData.case_status !== 2 && (
                <div className="row">
                  {/* Area field */}
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label>Area <span className="text-danger">*</span></label>
                      {isEditMode ? (
                        <input 
                          type="text" 
                          className={`form-control ${errors.case_area ? 'is-invalid' : ''}`}
                          value={formData.area?.area_name || formData.case_area || ''}
                          disabled
                        />
                      ) : (
                        <select
                          name="case_area"
                          value={selectedArea || ''}
                          onChange={handleAreaChange}
                          className={`form-select ${errors.case_area ? 'is-invalid' : ''}`}
                          disabled={!selectedLocation || loadingAreas}
                          required
                        >
                          <option value="">{loadingAreas ? 'Loading areas...' : 'Select Area'}</option>
                          {areaError ? (
                            <option value="" disabled>Error loading areas</option>
                          ) : (
                            areas.map((area) => (
                              <option key={area.id} value={area.id}>
                                {area.area_name} ({area.ems_location?.location_name || 'Unknown'})
                              </option>
                            ))
                          )}
                        </select>
                      )}
                      {errors.case_area && <div className="invalid-feedback">{errors.case_area}</div>}
                      {!loadingAreas && selectedLocation && areas.length === 0 && !areaError && (
                        <div className="text-muted small mt-1">No areas found for this location</div>
                      )}
                    </div>
                  </div>
                  {/* Medical Center field */}
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label>Transfer To<span className="text-danger">*</span></label>
                      {isEditMode ? (
                        <input 
                          type="text" 
                          className={`form-control ${errors.nearest_medical_center ? 'is-invalid' : ''}`}
                          value={formData.medical_center?.name || ''}
                          disabled
                        />
                      ) : (
                        <select
                          name="nearest_medical_center"
                          className={`form-control form-select ${errors.nearest_medical_center ? 'is-invalid' : ''}`}
                          value={formData.nearest_medical_center || ''}
                          onChange={handleChange}
                          disabled={isMedicalCentersLoading || medicalCenters.length === 0}
                          required
                        >
                          <option value="">{isMedicalCentersLoading ? 'Loading medical centers...' : 'Select Transfer To'}</option>
                          {medicalCenters.map((center) => (
                            <option key={center.id} value={center.id}>
                              {center.name} ({center.distance_km} km)
                            </option>
                          ))}
                        </select>
                      )}
                      {errors.nearest_medical_center && <div className="invalid-feedback">{errors.nearest_medical_center}</div>}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Injury Type and Age fields */}
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label>Type of Injury <span className="text-danger">*</span></label>
                    <input 
                      type="text" 
                      className={`form-control ${errors.injury_type ? 'is-invalid' : ''}`}
                      name="injury_type"
                      value={formData.injury_type || ''}
                      onChange={handleChange}
                      list="injuryTypes"
                      required
                    />
                    <datalist id="injuryTypes">
                      {injuryTypes.map((type, index) => (
                        <option key={index} value={type} />
                      ))}
                    </datalist>
                    {errors.injury_type && <div className="invalid-feedback">{errors.injury_type}</div>}
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label>Age</label>
                    <input 
                      type="number" 
                      className={`form-control ${errors.age ? 'is-invalid' : ''}`}
                      name="age"
                      value={formData.age || ''}
                      onChange={handleChange}
                      min="0"
                      max="120"
                      required
                    />
                    {errors.age && <div className="invalid-feedback">{errors.age}</div>}
                  </div>
                </div>
              </div>
              {/* Gender and Contact Number fields */}
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label>Gender</label>
                    <select 
                      className={`form-control form-select ${errors.gender ? 'is-invalid' : ''}`} 
                      name="gender"
                      value={formData.gender || ''}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                    {errors.gender && <div className="invalid-feedback">{errors.gender}</div>}
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label>Contact Number <span className="text-danger">*</span></label>
                    <input 
                      type="tel" 
                      className={`form-control ${errors.contact_number ? 'is-invalid' : ''}`} 
                      name="contact_number"
                      value={formData.contact_number || ''}
                      onChange={handleChange}
                      pattern="[0-9]{10,15}"
                      required
                    />
                    {errors.contact_number && <div className="invalid-feedback">{errors.contact_number}</div>}
                  </div>
                </div>
              </div>

              {/* Edit mode specific fields */}
              {isEditMode && (
                <>
                  <div className="row">
                    {/* Triage Card field */}
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label>Triage Card <span className="text-danger">*</span></label>
                        <select 
                          className={`form-control form-select ${errors.triage_card ? 'is-invalid' : ''}`}
                          name="triage_card"
                          value={formData.triage_card || ''}
                          onChange={handleChange}
                          required
                        >
                          <option value="">Select Triage Card</option>
                          <option value="Red">Red</option>
                          <option value="Yellow">Yellow</option>
                          <option value="Green">Green</option>
                          <option value="Black">Black</option>
                        </select>
                        {errors.triage_card && <div className="invalid-feedback">{errors.triage_card}</div>}
                      </div>
                    </div>
                    {/* Bracelet ID field with QR scanner */}
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label>Bracelet ID <span className="text-danger">*</span></label>
                        <div className="d-flex align-items-center">
                          <input 
                            type="text" 
                            className={`form-control ${errors.bracelet_id ? 'is-invalid' : ''}`}
                            name="bracelet_id"
                            value={formData.bracelet_id || ''}
                            onChange={handleChange}
                            required
                          />
                          <i
                            className="ti ti-scan text-primary fs-4 ms-3 d-lg-none d-block"
                            style={{ cursor: 'pointer' }}
                            onClick={() => setShowScanner(true)}
                          ></i>
                        </div>
                        {errors.bracelet_id && <div className="invalid-feedback">{errors.bracelet_id}</div>}
                      </div>
                    </div>
                  </div>
                  {/* Documents upload section */}
                  <div className="row">
                    <div className="col-md-12">
                      <label>Documents Upload</label>
                      {fileInputs.map((_, index) => (
                        <div key={index} className="mb-3 d-flex align-items-center flex-nowrap">
                          <div className="flex-grow-1 me-2">
                            <input
                              className={`form-control ${errors[`documents${index + 1}`] ? 'is-invalid' : ''}`}
                              type="file"
                              onChange={(e) => handleFileChange(e, index)}
                              accept="image/jpeg,image/jpg,image/png,image/webp,image/jfif,application/pdf"
                            />
                            {errors[`documents${index + 1}`] && (
                              <div className="invalid-feedback">{errors[`documents${index + 1}`]}</div>
                            )}
                          </div>
                          <div className="d-flex align-items-center">
                            {index === fileInputs.length - 1 && fileInputs.length < MAX_FILES && (
                              <button
                                type="button"
                                className="btn btn-sm btn-icon fs-5 me-2"
                                onClick={addFileInput}
                              >
                                <i className="ti ti-circle-plus text-success"></i>
                              </button>
                            )}
                            {fileInputs.length > 1 && (
                              <button
                                type="button"
                                className="btn btn-sm fs-5 btn-icon"
                                onClick={() => removeFileInput(index)}
                              >
                                <i className="ti ti-trash text-danger"></i>
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
            {/* Modal footer with action buttons */}
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-outline-danger" 
                onClick={handleClose}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn btn-primary" 
                onClick={handleSave}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    {isEditMode ? ' Updating...' : ' Saving...'}
                  </>
                ) : (
                  isEditMode ? 'Update' : 'Save'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* QR Code Scanner Modal */}
      {showScanner && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.8)', zIndex: '1055' }}>
          <div className="d-flex justify-content-end p-3">
            <i
              className="ti ti-x text-white fs-2"
              style={{ cursor: 'pointer' }}
              onClick={() => setShowScanner(false)}
            ></i>
          </div>
          <div className="d-flex justify-content-center align-items-center" style={{ height: '90%' }}>
            <div id="reader" className="border rounded" style={{ width: '100%', maxWidth: '400px' }}></div>
          </div>
        </div>
      )}
    </>
  );
};

export default CaseEditmodal;